import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, Button, Divider, Grid2, IconButton, Paper, styled, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, useTheme, } from '@mui/material';
import { IoMdClose } from 'react-icons/io'
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import moment from 'moment';
import { usePublishMonteCarloSimulationMutation } from 'store/slices/apis/monteCarloSimulationSlice';
import { usePermission } from 'hooks/usePermission';
import { MONTE_CARLO_SIMULATION_PUBLISH } from 'config';


const StyledTableHead = styled(TableCell)(({ theme }) => ({
    fontWeight: "400", fontSize: '16px', color: '#000', padding: '10px 14px 10px 14px',
    backgroundColor: '#F8F8F8', border: '2px solid #c5bbbb'
}));

const StyledTableRow = styled(TableCell)(({ theme }) => ({
    fontWeight: "400", fontSize: '13px', color: '#37352F', padding: '10px 14px 10px 14px', border: '2px solid #c5bbbb'
}));

const StyledTab = styled(Tab)(({ theme }) => ({
    textTransform: "none", // Prevent uppercase
    fontWeight: "400",
    fontSize: "14px",
    color: "#37352F",
    padding: '0px',
    minHeight: '0px',
    minWidth: '0px',
    margin: '0px 20px 5px 5px',
    "&.Mui-selected": {
        color: `#37352F`,
    }
}));


function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
        </div>
    );
}

export default function ResultMonteCarloSimulation(props) {
    const { open, cancel, rowData, openNotification, publish } = props;

    const [tabIndex, setTabIndex] = React.useState(0);
    const publishMOnteCarlo = usePermission(MONTE_CARLO_SIMULATION_PUBLISH);
    const theme = useTheme();
    const [resultData, setResultData] = React.useState(
        rowData?.resultData ? JSON.parse(rowData.resultData) : {}
    );

    const [publishFun, publishRes] = usePublishMonteCarloSimulationMutation();

    const histogramData = resultData?.simulationProgress?.map(date => [new Date(date?.IterationDate).getTime(), date?.IterationCount]);

    const costHistogramData = resultData?.costSimulationProgress?.map(result => [result?.SimulatedCost, result?.IterationCount]);




    const cumulativeData = resultData?.simulationProgress?.map((date) => {
        return [new Date(date?.IterationDate).getTime(), parseFloat(date?.CumulativeIterationPercentage.toFixed(2))]; // for line chart
    });

    const costCumulativeData = resultData?.costSimulationProgress?.map((result) => {
        return [result?.SimulatedCost, parseFloat(result?.CumulativeIterationPercentage.toFixed(2))]; // for line chart
    });

    const handleChange = (_, newValue) => {
        setTabIndex(newValue);
    };

    const options = {
        title: {
            text: ""
        },
        xAxis: {
            type: "datetime",
            labels: {
                format: "{value:%d/%m/%Y}"
            },
        },
        yAxis: [
            {
                title: { text: "" },
                gridLineWidth: 0,
            },
            {
                title: { text: "" },
                gridLineWidth: 0,
                opposite: true,
                labels: {
                    format: "{value}%"
                },
                tickPositioner: function () {
                    let positions = [0];
                    for (let i = 10; i <= 100; i += 10) {
                        positions.push(i);
                    }
                    return positions;
                },
            }
        ],
        series: [
            {
                type: "column",
                name: "Hits",
                data: histogramData,
                yAxis: 0,
                color: `${theme?.customSettings?.themeColor}`
            },
            {
                type: "spline",
                name: "Cumulative",
                data: cumulativeData,
                yAxis: 1,
                tooltip: {
                    valueSuffix: "%"
                },
                marker: {
                    enabled: true
                },
                color: "#415385"
            }
        ],
        tooltip: {
            shared: true,
            xDateFormat: "%d/%m/%Y"
        },
        credits: {
            enabled: false
        },
        responsive: {
            rules: [
                {
                    condition: {
                        maxWidth: 600 // apply on mobile screens
                    },
                    chartOptions: {
                        xAxis: {
                            labels: {
                                rotation: -45, // tilt labels for space
                                style: { fontSize: "10px" }
                            },
                            // tickPixelInterval: 150
                        },
                        yAxis: [
                            { labels: { style: { fontSize: "10px" } } },
                            { labels: { style: { fontSize: "10px" } } }
                        ],
                    }
                }
            ]
        }
    };

    const costOptions = {
        title: {
            text: ""
        },
        xAxis: {
            // type: "datetime",
            labels: {
                format: "{value}"
            },
        },
        yAxis: [
            {
                title: { text: "" },
                gridLineWidth: 0,
            },
            {
                title: { text: "" },
                gridLineWidth: 0,
                opposite: true,
                labels: {
                    format: "{value}%"
                },
                tickPositioner: function () {
                    let positions = [0];
                    for (let i = 10; i <= 100; i += 10) {
                        positions.push(i);
                    }
                    return positions;
                },
            }
        ],
        series: [
            {
                type: "column",
                name: "Hits",
                data: costHistogramData,
                yAxis: 0,
                color: `${theme?.customSettings?.themeColor}`
            },
            {
                type: "spline",
                name: "Cumulative",
                data: costCumulativeData,
                yAxis: 1,
                tooltip: {
                    valueSuffix: "%"
                },
                marker: {
                    enabled: true
                },
                color: "#415385"
            }
        ],
        tooltip: {
            shared: true,
            xDateFormat: "%d/%m/%Y"
        },
        credits: {
            enabled: false
        }
    };

    const handlePublish = () => {

        publishFun({ params: { publish: true }, id: rowData?.id });
    }

    React.useEffect(() => {
        if (publishRes?.isSuccess) {
            openNotification('Published successfully', 'success')
            cancel();
        }
    }, [publishRes?.isSuccess]);

    React.useEffect(() => {
        if (publishRes?.isError) {
            openNotification('Something went wrong!', 'error')
        }
    }, [publishRes?.isError]);

    return (
        <React.Fragment>
            <Dialog
                fullWidth={true}
                maxWidth={'lg'}
                open={open}
                sx={{
                    '& .MuiDialog-paper': {
                        overflowY: 'inherit'
                    }
                }}
            >
                <DialogTitle sx={{ fontSize: '20px', color: '#696969' }}>{`Monte Carlo Simulation Result`}</DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={cancel}
                    sx={(theme) => ({
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme.palette.grey[500],
                    })}
                >
                    <IoMdClose style={{ fontSize: '20px' }} />
                </IconButton>
                <Divider sx={{ w: 1 }} />
                <DialogContent>
                    <Box sx={{ width: '100%' }}>
                        <Grid2 size={12} sx={{ marginBottom: 2 }}>
                            <Tabs value={tabIndex} onChange={handleChange} sx={{
                                minHeight: "0px",
                                "& .MuiTabs-indicator": { backgroundColor: `${theme?.customSettings?.themeColor}` },
                            }}>
                                <StyledTab label="Schedule" sx={{ textTransform: 'none' }} />
                                <StyledTab label="Cost" sx={{ textTransform: 'none' }} />
                            </Tabs>
                        </Grid2>
                        <CustomTabPanel value={tabIndex} index={0}>
                            <Grid2 container spacing={2}>
                                <Grid2 size={{ xs: 12, sm: 12, md: 9 }}>
                                    <HighchartsReact highcharts={Highcharts} options={options} />
                                </Grid2>
                                <Grid2 size={{ xs: 12, sm: 12, md: 3 }}>
                                    <TableContainer>
                                        <Table>
                                            <TableRow>
                                                <StyledTableHead colSpan={2}>
                                                    Data
                                                </StyledTableHead>
                                            </TableRow>
                                            <TableRow>
                                                <StyledTableRow>
                                                    Finish Date
                                                </StyledTableRow>
                                                <StyledTableRow>
                                                    {resultData?.PlanFinishDate ? moment(resultData?.PlanFinishDate).format('DD/MM/YYYY') : "-"}
                                                </StyledTableRow>
                                            </TableRow>
                                            <TableRow>
                                                <StyledTableHead colSpan={2}>
                                                    Analysis
                                                </StyledTableHead>
                                            </TableRow>
                                            <TableRow>
                                                <StyledTableRow>
                                                    No Of Iterations
                                                </StyledTableRow>
                                                <StyledTableRow>
                                                    {rowData?.numberOfIterations ? rowData?.numberOfIterations : "-"}
                                                </StyledTableRow>
                                            </TableRow>
                                            <TableRow>
                                                <StyledTableHead colSpan={2}>
                                                    Output Statistics
                                                </StyledTableHead>
                                            </TableRow>
                                            <TableRow>
                                                <StyledTableRow>
                                                    {rowData?.minimum ? `${rowData?.taskMinDurationSource} % Probablity` : ""}
                                                </StyledTableRow>
                                                <StyledTableRow>
                                                    {resultData?.MinimumCopletionDate ? moment(resultData?.MinimumCopletionDate).format('DD/MM/YYYY') : "-"}
                                                </StyledTableRow>
                                            </TableRow>
                                            <TableRow>
                                                <StyledTableRow>
                                                    {rowData?.mostLikely ? `${rowData?.taskMostLikelyDurationSource} % Probablity` : ""}
                                                </StyledTableRow>
                                                <StyledTableRow>
                                                    {resultData?.MostLikelyCopletionDate ? moment(resultData?.MostLikelyCopletionDate).format('DD/MM/YYYY') : "-"}
                                                </StyledTableRow>
                                            </TableRow>
                                            <TableRow>
                                                <StyledTableRow>
                                                    {rowData?.maximum ? `${rowData?.taskMaxDurationSource} % Probablity` : ""}
                                                </StyledTableRow>
                                                <StyledTableRow>
                                                    {resultData?.MaximumCopletionDate ? moment(resultData?.MaximumCopletionDate).format('DD/MM/YYYY') : "-"}
                                                </StyledTableRow>
                                            </TableRow>
                                        </Table>
                                    </TableContainer>
                                </Grid2>
                            </Grid2>
                        </CustomTabPanel>
                        <CustomTabPanel value={tabIndex} index={1}>
                            <Grid2 container spacing={2}>
                                <Grid2 size={{ xs: 12, sm: 12, md: 9 }}>
                                    <HighchartsReact highcharts={Highcharts} options={costOptions} />
                                </Grid2>
                                <Grid2 size={{ xs: 12, sm: 12, md: 3 }}>
                                    <TableContainer>
                                        <Table>
                                            <TableRow>
                                                <StyledTableHead colSpan={2}>
                                                    Data
                                                </StyledTableHead>
                                            </TableRow>
                                            <TableRow>
                                                <StyledTableRow>
                                                    Budget Cost
                                                </StyledTableRow>
                                                <StyledTableRow>
                                                    {resultData?.BudgetCost?.toFixed(2)}
                                                </StyledTableRow>
                                            </TableRow>
                                            <TableRow>
                                                <StyledTableHead colSpan={2}>
                                                    Analysis
                                                </StyledTableHead>
                                            </TableRow>
                                            <TableRow>
                                                <StyledTableRow>
                                                    No Of Iterations
                                                </StyledTableRow>
                                                <StyledTableRow>
                                                    {rowData?.numberOfIterations}
                                                </StyledTableRow>
                                            </TableRow>
                                            <TableRow>
                                                <StyledTableHead colSpan={2}>
                                                    Statistics
                                                </StyledTableHead>
                                            </TableRow>
                                            <TableRow>
                                                <StyledTableRow>
                                                    Minimum ({`P${rowData?.taskMinDurationSource}`})
                                                </StyledTableRow>
                                                <StyledTableRow>
                                                    {parseFloat(resultData?.MinimumCost?.toFixed(2))}
                                                </StyledTableRow>
                                            </TableRow>
                                            <TableRow>
                                                <StyledTableRow>
                                                    Most Likely ({`P${rowData?.taskMostLikelyDurationSource}`})
                                                </StyledTableRow>
                                                <StyledTableRow>
                                                    {parseFloat(resultData?.MostLikelyCost?.toFixed(2))}
                                                </StyledTableRow>
                                            </TableRow>
                                            <TableRow>
                                                <StyledTableRow>
                                                    Maximum ({`P${rowData?.taskMaxDurationSource}`})
                                                </StyledTableRow>
                                                <StyledTableRow>
                                                    {parseFloat(resultData?.MaximumCost?.toFixed(2))}
                                                </StyledTableRow>
                                            </TableRow>
                                        </Table>
                                    </TableContainer>
                                </Grid2>
                            </Grid2>
                        </CustomTabPanel>
                        {publish &&
                            <Grid2 size={12}>
                                {!rowData?.publish &&
                                    publishMOnteCarlo &&
                                    <Button
                                        type='button'
                                        variant="contained"
                                        loading={publishRes?.isLoading}
                                        onClick={handlePublish}
                                        sx={{
                                            backgroundColor: `${theme?.customSettings?.themeColor}`,
                                            color: '#FFF', padding: '7px 20px',
                                            fontWeight: '500', textTransform: 'none',
                                            fontSize: '16px', float: 'right'
                                        }}>
                                        Publish
                                    </Button>
                                }
                            </Grid2>
                        }

                    </Box>
                </DialogContent>
            </Dialog>
        </React.Fragment >
    );
}